import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { AudioRecorder } from "../services/audioService";
import { LONG_RECORDING_WARNING_SECONDS } from "../config";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
// Import FFmpeg assets
import ffmpegCoreJS from "../assets/ffmpeg/ffmpeg-core.js?url";
import ffmpegCoreWASM from "../assets/ffmpeg/ffmpeg-core.wasm?url";
import "../styles/DashboardPage.css";

// Define interfaces for the transcript data
interface TranscriptWord {
  start_time: number;
  end_time: number;
  word: string;
  confidence: number;
  speaker_label: string;
}

interface TranscriptAlternative {
  result_index: number;
  alt_index: number;
  transcript: string;
  words: TranscriptWord[];
}

interface TranscriptData {
  alternatives: TranscriptAlternative[];
  is_final: boolean;
}

interface TranscriptItem {
  id: number;
  timestamp: Date;
  data: TranscriptData;
  rawText: string;
}

// Define recording states
enum RecordingState {
  IDLE = "idle", // Initial state, ready to prepare for recording
  PREPARING = "preparing", // Preparing to record (initializing)
  RECORDING = "recording", // Currently recording
  STOPPING = "stopping", // Stopping the recording
  READY = "ready", // Recording stopped, showing results, ready to prepare again
}

// Define available audio formats
interface AudioFormatOption {
  label: string;
  value: string;
  extension: string;
  mimeType: string;
}

const AUDIO_FORMATS: AudioFormatOption[] = [
  { label: "Original Format", value: "original", extension: "", mimeType: "" },
  { label: "MP3", value: "mp3", extension: ".mp3", mimeType: "audio/mpeg" },
  { label: "WAV", value: "wav", extension: ".wav", mimeType: "audio/wav" },
  { label: "AAC", value: "aac", extension: ".aac", mimeType: "audio/aac" },
  { label: "FLAC", value: "flac", extension: ".flac", mimeType: "audio/flac" },
  { label: "OGG", value: "ogg", extension: ".ogg", mimeType: "audio/ogg" },
];

// Add a new AudioVisualizer component
const AudioVisualizer: React.FC<{
  isRecording: boolean;
  stream: MediaStream | null;
}> = ({ isRecording, stream }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // Setup audio context and analyzer
  useEffect(() => {
    if (isRecording && stream) {
      // Create audio context and analyzer
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      // Create analyzer node
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      // Create data array for visualization
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;

      // Connect the stream to the analyzer
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceRef.current = source;

      // Start animation
      startVisualization();
    }

    return () => {
      // Cleanup
      stopVisualization();
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(console.error);
        audioContextRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      analyserRef.current = null;
      dataArrayRef.current = null;
    };
  }, [isRecording, stream]);

  // Draw visualization
  const draw = useCallback(() => {
    if (!canvasRef.current || !analyserRef.current || !dataArrayRef.current)
      return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    // Get canvas dimensions
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Get frequency data
    analyser.getByteTimeDomainData(dataArray);

    // Set line style
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#4CAF50";

    // Draw waveform
    ctx.beginPath();

    const sliceWidth = width / dataArray.length;
    let x = 0;

    for (let i = 0; i < dataArray.length; i++) {
      const v = dataArray[i] / 128.0; // normalize to 0-2
      const y = (v * height) / 2;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }

      x += sliceWidth;
    }

    ctx.lineTo(width, height / 2);
    ctx.stroke();

    // Continue animation
    animationRef.current = requestAnimationFrame(draw);
  }, []);

  // Start visualization
  const startVisualization = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(draw);
  }, [draw]);

  // Stop visualization
  const stopVisualization = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  // Resize canvas to match container
  useEffect(() => {
    const resizeCanvas = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const container = canvas.parentElement;
        if (container) {
          canvas.width = container.clientWidth;
          canvas.height = 100; // Fixed height
        }
      }
    };

    // Initial resize
    resizeCanvas();

    // Add resize listener
    window.addEventListener("resize", resizeCanvas);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <div className="audio-visualizer-container">
      {isRecording ? (
        <canvas ref={canvasRef} className="audio-visualizer"></canvas>
      ) : (
        <div className="audio-visualizer-placeholder">
          <div className="placeholder-line"></div>
        </div>
      )}
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const { accessToken, logout } = useAuth();
  const navigate = useNavigate();

  // Replace isRecording, isConnecting, isStoppingRecording with a single state
  const [recordingState, setRecordingState] = useState<RecordingState>(
    RecordingState.IDLE
  );

  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioFormat, setAudioFormat] = useState<string>("");
  const [transcripts, setTranscripts] = useState<TranscriptItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [stopAttempts, setStopAttempts] = useState(0);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);

  // FFmpeg state
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<string>("original");
  const [conversionProgress, setConversionProgress] = useState(0);

  const recorderRef = useRef<AudioRecorder | null>(null);
  const timerRef = useRef<number | null>(null);
  const timerStartTimeRef = useRef<number>(0);
  const transcriptIdCounter = useRef(0);
  const stopTimeoutRef = useRef<number | null>(null);
  const ffmpegRef = useRef<FFmpeg | null>(null);

  // Helper function to check if we can start recording
  const canStartRecording = () => {
    return recordingState === RecordingState.IDLE;
  };

  // Helper function to check if we can stop recording
  const canStopRecording = () => {
    return recordingState === RecordingState.RECORDING;
  };

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!accessToken) {
      navigate("/login");
      return;
    }

    // Initialize recorder on component mount
    const initializeRecorder = async () => {
      try {
        console.log("Initializing recorder on component mount");
        recorderRef.current = new AudioRecorder(accessToken as string);
        setRecordingState(RecordingState.IDLE);
      } catch (err) {
        console.error("Failed to initialize recorder:", err);
        setError("Failed to initialize recorder. Please refresh the page.");
      }
    };

    initializeRecorder();

    // Cleanup on unmount
    return () => {
      if (recorderRef.current) {
        recorderRef.current.cleanupResources();
      }

      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }

      if (stopTimeoutRef.current) {
        window.clearTimeout(stopTimeoutRef.current);
      }

      // Properly revoke the object URL to prevent memory leaks
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [accessToken, navigate, audioUrl]);

  // Add a debug effect to log state changes
  useEffect(() => {
    console.log("Recording state changed:", recordingState);
  }, [recordingState]);

  // Function to prepare for recording (new intermediate step)
  const prepareForRecording = async () => {
    console.log("Preparing for recording...");

    // Clear previous recording data
    setRecordingState(RecordingState.PREPARING);
    setError(null);
    setTranscripts([]);
    transcriptIdCounter.current = 0;
    setRecordingTime(0);
    setStopAttempts(0);

    // Revoke previous audio URL if it exists
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    // Clear audio blob
    setAudioBlob(null);
    setAudioFormat("");

    // Create a fresh recorder instance
    try {
      // Dispose of any existing recorder
      if (recorderRef.current) {
        await recorderRef.current.cleanupResources();
      }

      // Create a new recorder
      recorderRef.current = new AudioRecorder(accessToken as string);

      // Ready to record
      setRecordingState(RecordingState.IDLE);
    } catch (err) {
      console.error("Failed to prepare for recording:", err);
      setError("Failed to prepare for recording. Please try again.");
      setRecordingState(RecordingState.READY); // Go back to ready state
    }
  };

  const startRecording = async () => {
    if (!canStartRecording()) {
      console.warn("Cannot start recording in current state:", recordingState);
      return;
    }

    if (!recorderRef.current) {
      console.error("No recorder reference available");
      setError("Recording initialization failed: No recorder available");
      return;
    }

    try {
      // Clear transcripts when starting a new recording
      setTranscripts([]);
      transcriptIdCounter.current = 0;

      // Update state to show we're connecting
      setRecordingState(RecordingState.PREPARING);

      // Get microphone stream for visualizer
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);

      // Start the recording process
      await recorderRef.current.startRecording({
        onTranscriptReceived: (transcript) => {
          console.log("Transcript received, length:", transcript.length);

          // Check if the transcript is valid JSON
          const isJson = transcript.startsWith("{") && transcript.endsWith("}");

          if (isJson) {
            try {
              // Try to parse the JSON
              const parsedData = JSON.parse(transcript) as TranscriptData;

              // Create a new transcript item with a unique ID and timestamp
              const newTranscript: TranscriptItem = {
                id: ++transcriptIdCounter.current,
                timestamp: new Date(),
                data: parsedData,
                rawText: transcript,
              };

              // Add the new transcript to the beginning of the array (newest first)
              setTranscripts((prev) => [newTranscript, ...prev]);
            } catch (error) {
              console.error("Error parsing transcript JSON:", error);
              // If parsing fails, add as plain text
              const newTranscript: TranscriptItem = {
                id: ++transcriptIdCounter.current,
                timestamp: new Date(),
                data: {
                  alternatives: [
                    {
                      result_index: 0,
                      alt_index: 0,
                      transcript: transcript,
                      words: [],
                    },
                  ],
                  is_final: true,
                },
                rawText: transcript,
              };

              setTranscripts((prev) => [newTranscript, ...prev]);
            }
          } else {
            // Not JSON, add as plain text
            const newTranscript: TranscriptItem = {
              id: ++transcriptIdCounter.current,
              timestamp: new Date(),
              data: {
                alternatives: [
                  {
                    result_index: 0,
                    alt_index: 0,
                    transcript: transcript,
                    words: [],
                  },
                ],
                is_final: true,
              },
              rawText: transcript,
            };

            setTranscripts((prev) => [newTranscript, ...prev]);
          }
        },
        onError: (err) => {
          console.error("Recording error:", err);

          // Provide more specific error messages for common WebSocket issues
          let errorMessage = `${err.name}: ${err.message}`;

          if (err.message.includes("WebSocket connection")) {
            errorMessage =
              "Failed to connect to the transcription server. Please check your internet connection and try again.";
          } else if (err.message.includes("timeout")) {
            errorMessage =
              "Connection to the transcription server timed out. The server might be down or your internet connection is unstable.";
          } else if (
            err.message.includes("getUserMedia") ||
            err.message.includes("Permission")
          ) {
            errorMessage =
              "Microphone access denied. Please allow microphone access in your browser settings.";
          }

          setError(errorMessage);

          // If we're recording, stop it
          if (recordingState === RecordingState.RECORDING) {
            stopRecording();
          } else {
            // Just reset the state
            setRecordingState(RecordingState.IDLE);
          }
        },
      });

      console.log("Recording started successfully");

      // Reset recording time and store start time
      setRecordingTime(0);
      timerStartTimeRef.current = Date.now();

      // Start timer with more accurate tracking
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }

      timerRef.current = window.setInterval(() => {
        const elapsedSeconds = Math.floor(
          (Date.now() - timerStartTimeRef.current) / 1000
        );
        setRecordingTime(elapsedSeconds);
      }, 1000);

      // Update state to recording
      setRecordingState(RecordingState.RECORDING);
    } catch (err) {
      console.error("Failed to start recording:", err);

      // Provide user-friendly error message
      let errorMessage = "Failed to start recording";

      if (err instanceof Error) {
        if (err.message.includes("WebSocket")) {
          errorMessage =
            "Failed to connect to the transcription server. Please check your internet connection and try again.";
        } else if (
          err.message.includes("getUserMedia") ||
          err.message.includes("Permission")
        ) {
          errorMessage =
            "Microphone access denied. Please allow microphone access in your browser settings.";
        } else {
          errorMessage = `${err.name}: ${err.message}`;
        }
      }

      setError(errorMessage);
      setRecordingState(RecordingState.IDLE);
    }
  };

  const stopRecording = async () => {
    if (!canStopRecording()) {
      console.warn("Cannot stop recording in current state:", recordingState);
      return;
    }

    if (!recorderRef.current) {
      console.warn("No recorder reference available");
      setRecordingState(RecordingState.READY);
      return;
    }

    // Update state
    setRecordingState(RecordingState.STOPPING);
    setStopAttempts((prev) => prev + 1);

    // Clear any existing timeout
    if (stopTimeoutRef.current) {
      window.clearTimeout(stopTimeoutRef.current);
    }

    // Set a timeout for force stop
    stopTimeoutRef.current = window.setTimeout(() => {
      console.warn("Force stopping recording after timeout");
      forceStopRecording();
    }, 5000);

    try {
      // Stop the timer
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Stop the recording
      console.log("Calling stopRecording on recorder");
      const blob = await recorderRef.current.stopRecording();
      console.log("Recording stopped successfully, blob size:", blob.size);

      // Clear the force stop timeout
      if (stopTimeoutRef.current) {
        window.clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }

      // Process the audio blob
      if (blob && blob.size > 0) {
        setAudioBlob(blob);

        // Determine the audio format
        let format = "Unknown format";
        if (blob.type) {
          const mimeType = blob.type.toLowerCase();
          if (mimeType.includes("webm")) {
            format = "WebM Audio";
          } else if (mimeType.includes("mp4")) {
            format = "MP4 Audio";
          } else if (mimeType.includes("ogg")) {
            format = "Ogg Audio";
          } else if (mimeType.includes("wav")) {
            format = "WAV Audio";
          } else if (mimeType.includes("mpeg") || mimeType.includes("mp3")) {
            format = "MP3 Audio";
          } else {
            format = `Audio (${mimeType})`;
          }
        }
        setAudioFormat(format);

        // Create URL for audio playback
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
      } else {
        console.warn("Received empty blob from stopRecording");
      }

      // Clear audio stream
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
        setAudioStream(null);
      }

      // Update state to ready (showing results)
      setRecordingState(RecordingState.READY);
      setStopAttempts(0);
    } catch (err) {
      console.error("Failed to stop recording:", err);

      // Force stop if normal stop fails
      forceStopRecording();

      // Show error to user
      setError(
        err instanceof Error
          ? `${err.name}: ${err.message}`
          : "Failed to stop recording"
      );
    }
  };

  const forceStopRecording = async () => {
    console.log("Force stopping recording");

    // Clean up timers
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (stopTimeoutRef.current) {
      window.clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }

    // Try to get any recorded audio
    if (recorderRef.current) {
      try {
        // Get any available audio
        const audioData = await recorderRef.current.getRecordedAudio();
        console.log("Retrieved audio data during force stop:", audioData);

        if (audioData && audioData.size > 0) {
          // Set the audio blob
          setAudioBlob(audioData);

          // Determine format
          let format = "Unknown format";
          if (audioData.type) {
            const mimeType = audioData.type.toLowerCase();
            if (mimeType.includes("webm")) {
              format = "WebM Audio";
            } else if (mimeType.includes("mp4")) {
              format = "MP4 Audio";
            } else if (mimeType.includes("ogg")) {
              format = "Ogg Audio";
            } else if (mimeType.includes("wav")) {
              format = "WAV Audio";
            } else if (mimeType.includes("mpeg") || mimeType.includes("mp3")) {
              format = "MP3 Audio";
            } else {
              format = `Audio (${mimeType})`;
            }
          }
          setAudioFormat(format);

          // Create URL for audio playback
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
          }
          const url = URL.createObjectURL(audioData);
          setAudioUrl(url);
        }
      } catch (e) {
        console.error("Error retrieving audio data during force stop:", e);
      }

      // Clean up the recorder
      try {
        recorderRef.current.cleanupResources();
      } catch (e) {
        console.error("Error during force cleanup:", e);
      }
    }

    // Clear audio stream
    if (audioStream) {
      audioStream.getTracks().forEach((track) => track.stop());
      setAudioStream(null);
    }

    // Update state to ready (showing results)
    setRecordingState(RecordingState.READY);
    setStopAttempts(0);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const formatTimestamp = (date: Date): string => {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Initialize FFmpeg
  useEffect(() => {
    const loadFFmpeg = async () => {
      try {
        // Create FFmpeg instance
        const ffmpeg = new FFmpeg();

        // Set up logging and progress tracking
        ffmpeg.on("log", ({ message }) => {
          console.log("FFmpeg log:", message);
        });

        ffmpeg.on("progress", ({ progress }) => {
          setConversionProgress(Math.round(progress * 100));
        });

        console.log("Loading FFmpeg with:", {
          coreURL: ffmpegCoreJS,
          wasmURL: ffmpegCoreWASM,
        });

        // Load FFmpeg core using imported assets
        await ffmpeg.load({
          coreURL: ffmpegCoreJS,
          wasmURL: ffmpegCoreWASM,
        });

        ffmpegRef.current = ffmpeg;
        setFfmpegLoaded(true);
        console.log("FFmpeg loaded successfully");
      } catch (error) {
        console.error("Failed to load FFmpeg:", error);
        setError(
          "Failed to load audio conversion tools. Some features may not work."
        );
      }
    };

    loadFFmpeg();

    // Cleanup
    return () => {
      if (ffmpegRef.current) {
        ffmpegRef.current.terminate();
      }
    };
  }, []);

  // Convert audio to selected format
  const convertAudio = async (format: string): Promise<Blob | null> => {
    if (!ffmpegRef.current || !audioBlob) {
      console.error("FFmpeg not loaded or no audio blob available");
      return null;
    }

    try {
      setIsConverting(true);
      setConversionProgress(0);

      const ffmpeg = ffmpegRef.current;

      // Determine input format
      let inputFormat = "webm";
      if (audioBlob.type) {
        const mimeType = audioBlob.type.toLowerCase();
        if (mimeType.includes("mp4")) inputFormat = "mp4";
        else if (mimeType.includes("ogg")) inputFormat = "ogg";
        else if (mimeType.includes("wav")) inputFormat = "wav";
        else if (mimeType.includes("mpeg") || mimeType.includes("mp3"))
          inputFormat = "mp3";
      }

      // Create input file
      const inputFileName = `input.${inputFormat}`;
      const outputFileName = `output.${format}`;

      // Write input file to memory
      await ffmpeg.writeFile(inputFileName, await fetchFile(audioBlob));

      // Run FFmpeg command
      await ffmpeg.exec([
        "-i",
        inputFileName,
        "-c:a",
        format === "mp3"
          ? "libmp3lame"
          : format === "aac"
          ? "aac"
          : format === "flac"
          ? "flac"
          : format === "ogg"
          ? "libvorbis"
          : "pcm_s16le",
        "-b:a",
        "128k",
        outputFileName,
      ]);

      // Read output file
      const data = await ffmpeg.readFile(outputFileName);

      // Get the appropriate MIME type
      const formatOption = AUDIO_FORMATS.find((opt) => opt.value === format);
      const mimeType = formatOption?.mimeType || "audio/mpeg";

      // Create blob from output data
      const outputBlob = new Blob([data], { type: mimeType });

      setIsConverting(false);
      return outputBlob;
    } catch (error) {
      console.error("Error converting audio:", error);
      setIsConverting(false);
      return null;
    }
  };

  const downloadAudio = async () => {
    if (!audioBlob || !audioUrl) {
      console.error("Cannot download: No audio blob or URL available");
      setError("Cannot download audio: No recording available");
      return;
    }

    try {
      let downloadBlob = audioBlob;
      let fileExtension = ".webm"; // Default extension

      // If a format other than original is selected, convert the audio
      if (selectedFormat !== "original" && ffmpegLoaded) {
        setIsConverting(true);

        // Find the selected format option
        const formatOption = AUDIO_FORMATS.find(
          (opt) => opt.value === selectedFormat
        );

        if (formatOption) {
          // Convert audio to selected format
          const convertedBlob = await convertAudio(selectedFormat);

          if (convertedBlob) {
            downloadBlob = convertedBlob;
            fileExtension = formatOption.extension;
          } else {
            setError(
              `Failed to convert to ${formatOption.label} format. Using original format instead.`
            );
          }
        }

        setIsConverting(false);
      } else {
        // Determine the file extension based on the MIME type of the original blob
        if (audioBlob.type) {
          const mimeType = audioBlob.type.toLowerCase();

          if (mimeType.includes("mp4")) {
            fileExtension = ".mp4";
          } else if (mimeType.includes("ogg")) {
            fileExtension = ".ogg";
          } else if (mimeType.includes("wav")) {
            fileExtension = ".wav";
          } else if (mimeType.includes("mpeg") || mimeType.includes("mp3")) {
            fileExtension = ".mp3";
          } else if (mimeType.includes("webm")) {
            fileExtension = ".webm";
          }
        }
      }

      console.log(
        `Downloading audio with MIME type: ${downloadBlob.type}, size: ${downloadBlob.size} bytes`
      );

      // Create download URL for the blob
      const downloadUrl = URL.createObjectURL(downloadBlob);

      // Create download link
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `recording-${new Date().toISOString()}${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up the URL
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error downloading audio:", err);
      setError("Failed to download audio recording");
      setIsConverting(false);
    }
  };

  // Function to render a transcript item
  const renderTranscriptItem = (item: TranscriptItem) => {
    const { data, timestamp, id } = item;

    // Get the best alternative (usually the first one)
    const bestAlternative = data.alternatives[0];

    return (
      <div key={id} className="transcript-item">
        <div className="transcript-header">
          <span className="transcript-timestamp">
            {formatTimestamp(timestamp)}
          </span>
          <span
            className={`transcript-status ${
              data.is_final ? "final" : "interim"
            }`}
          >
            {data.is_final ? "Final" : "Interim"}
          </span>
        </div>

        <div className="transcript-content">
          <div className="transcript-text">{bestAlternative.transcript}</div>

          {bestAlternative.words.length > 0 && (
            <div className="transcript-words">
              {bestAlternative.words.map((word, wordIndex) => (
                <span
                  key={wordIndex}
                  className="transcript-word"
                  title={`Confidence: ${(word.confidence * 100).toFixed(1)}%`}
                >
                  {word.word.replace("▁", " ")}
                </span>
              ))}
            </div>
          )}

          {data.alternatives.length > 1 && (
            <div className="transcript-alternatives">
              <details>
                <summary>
                  Alternative transcripts ({data.alternatives.length - 1})
                </summary>
                <ul>
                  {data.alternatives.slice(1).map((alt, altIndex) => (
                    <li key={altIndex}>{alt.transcript}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render the appropriate button based on the current state
  const renderActionButton = () => {
    switch (recordingState) {
      case RecordingState.IDLE:
        return (
          <button className="record-button start" onClick={startRecording}>
            Start Recording
          </button>
        );

      case RecordingState.PREPARING:
        return (
          <button className="record-button preparing" disabled>
            Preparing...
          </button>
        );

      case RecordingState.RECORDING:
        return (
          <button className="record-button stop" onClick={stopRecording}>
            Stop Recording
          </button>
        );

      case RecordingState.STOPPING:
        return (
          <button className="record-button stopping" disabled>
            Stopping...
          </button>
        );

      case RecordingState.READY:
        return (
          <button
            className="record-button prepare"
            onClick={prepareForRecording}
          >
            Prepare New Recording
          </button>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Audio Transcription Dashboard</h1>
        <button className="logout-button" onClick={handleLogout}>
          Logout
        </button>
      </header>

      <main className="dashboard-content">
        <div className="recording-section">
          <div className="recording-controls">
            <div className="button-group">
              {renderActionButton()}

              {recordingState === RecordingState.RECORDING &&
                stopAttempts > 0 && (
                  <button
                    className="force-stop-button"
                    onClick={forceStopRecording}
                    title="Use this if normal stop is not working"
                  >
                    Force Stop
                  </button>
                )}
            </div>

            {recordingState === RecordingState.RECORDING && (
              <div className="recording-status">
                Recording... {formatTime(recordingTime)}
              </div>
            )}

            {recordingState === RecordingState.STOPPING && (
              <div className="recording-status">
                Stopping recording
                {stopAttempts > 0 ? ` (attempt ${stopAttempts})` : ""}...
              </div>
            )}

            {recordingState === RecordingState.READY && (
              <div className="recording-status success">
                Recording completed. Click "Prepare New Recording" to record
                again.
              </div>
            )}
          </div>

          {/* Add audio visualizer */}
          <AudioVisualizer
            isRecording={recordingState === RecordingState.RECORDING}
            stream={audioStream}
          />

          {recordingTime > LONG_RECORDING_WARNING_SECONDS &&
            recordingState === RecordingState.RECORDING && (
              <div className="recording-warning">
                <p>
                  <strong>Warning:</strong> Long recordings may cause
                  performance issues. Consider stopping and starting a new
                  recording session.
                </p>
              </div>
            )}

          {error && (
            <div className="error-message">
              <h3>Error:</h3>
              <p>{error}</p>
              <div className="error-help">
                <p>Possible solutions:</p>
                <ul>
                  <li>Check your internet connection</li>
                  <li>Make sure your microphone is working and allowed</li>
                  <li>Try logging out and logging back in</li>
                  <li>Your access token might have expired</li>
                </ul>
              </div>
            </div>
          )}

          {/* Only show audio playback when not recording */}
          {audioBlob &&
            audioBlob.size > 0 &&
            recordingState !== RecordingState.RECORDING &&
            recordingState !== RecordingState.PREPARING && (
              <div className="audio-playback">
                <h3>Recording Completed</h3>
                <div className="audio-format">Format: {audioFormat}</div>
                {audioUrl && <audio controls src={audioUrl}></audio>}

                {/* Format selection and download section */}
                <div className="download-section">
                  <div className="format-selection">
                    <label htmlFor="format-select">Download Format:</label>
                    <select
                      id="format-select"
                      value={selectedFormat}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                      disabled={isConverting}
                    >
                      {AUDIO_FORMATS.map((format) => (
                        <option key={format.value} value={format.value}>
                          {format.label}
                        </option>
                      ))}
                    </select>

                    {!ffmpegLoaded && selectedFormat !== "original" && (
                      <div className="format-warning">
                        FFmpeg is loading... Format conversion will be available
                        soon.
                      </div>
                    )}
                  </div>

                  {isConverting && (
                    <div className="conversion-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${conversionProgress}%` }}
                        ></div>
                      </div>
                      <div className="progress-text">
                        Converting: {conversionProgress}%
                      </div>
                    </div>
                  )}

                  <button
                    className="download-button"
                    onClick={downloadAudio}
                    disabled={!audioUrl || isConverting}
                  >
                    {isConverting ? "Converting..." : "Download Recording"}
                  </button>
                </div>
              </div>
            )}
        </div>

        <div className="transcript-section">
          <h2>Live Transcripts</h2>
          {transcripts.length > 0 ? (
            <div className="transcripts-list">
              {transcripts.map(renderTranscriptItem)}
            </div>
          ) : (
            <div className="no-transcripts">
              {recordingState === RecordingState.RECORDING
                ? "Waiting for transcripts..."
                : "Start recording to see transcripts"}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
